import React, { useMemo, useState } from "react";
import { Box, Button, Tab, Tabs } from "@mui/material";
import { a11yProps } from "../../../../utils/a11yProps";
import SearchBar from "../../../../Components/inputTypes/searchBar/searchBar";
import { ReactComponent as FilterColumnIcon } from "../../../../Assets/filterColumns.svg";
import ColumnsListCheckBox from "../../../../Components/CustomTabs/ColumnsList/ColumnsListCheckBox";
import CustomTabsStyles from "../../../../Components/CustomTabs/CustomTabsStyles";
import FilterIcon from "../../../../Components/Icons/filterIcon";
import { fixedColumns } from "../../../../constants/InsightPageColumnsInfo";
import { useInsights } from "../../../../Context/insightContext";
import { InsightsFilterPopup } from "./components/insightsFilterPopup/insightsFilterPopup.index";
import { InsightsFilterShowcase } from "./components/insightsFilterShowcase/insightsFilterShowcase.index";
import { InsightsHeaderProps } from "./insightsHeader.models";
import { countActiveInsightsFilters } from "./insightsHeader.utils";
import styles from "./insightsHeader.module.css";

export const InsightsHeader = ({
    onSearch,
    searchValue,
    totalTableInfo,
    visibleColumns,
    setVisibleColumns,
}: InsightsHeaderProps) => {
    const { insightsUiFilters, filterInfoStatic } = useInsights();
    const { tabStyles } = CustomTabsStyles().classes;
    const [tabValue, setTabValue] = useState(0);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isFilterPopupOpen, setIsFilterPopupOpen] = useState(false);

    const availableFilters = filterInfoStatic.current ?? [];
    const areFiltersReady = availableFilters.length > 0;
    const filterCount = useMemo(
        () => countActiveInsightsFilters(insightsUiFilters),
        [insightsUiFilters]
    );

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const isColumnVisible = (columnId: string) =>
        Boolean(visibleColumns?.includes(columnId));

    const handleColumnToggle = (columnId: string) => {
        if (fixedColumns.includes(columnId)) {
            return;
        }

        setVisibleColumns((prev) => {
            if (!prev) {
                return prev;
            }

            return prev.includes(columnId)
                ? prev.filter((id) => id !== columnId)
                : [...prev, columnId];
        });
    };

    const handleSelectAll = () => {
        setVisibleColumns(totalTableInfo);
    };

    const handleUnselectApp = () => {
        setVisibleColumns(fixedColumns);
    };

    const allBoxSelected = totalTableInfo.length === (visibleColumns ?? []).length;

    return (
        <Box className={styles.insightsHeader}>
            <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="application tabs"
                className={tabStyles}
            >
                <Tab className={styles.labelTxt} label="All Applications" {...a11yProps(0)} />
            </Tabs>
            <div className={styles.headerRight}>
                <InsightsFilterShowcase />
                <button
                    type="button"
                    className={styles.filterTrigger}
                    onClick={() => setIsFilterPopupOpen(true)}
                    disabled={!areFiltersReady}
                    aria-label="Filters"
                >
                    <FilterIcon />
                    {filterCount > 0 && (
                        <span className={styles.filterBadge}>{filterCount}</span>
                    )}
                </button>
                <SearchBar
                    searchText={searchValue}
                    onSearch={onSearch}
                />
                <Button
                    aria-controls={anchorEl ? "column-menu" : undefined}
                    aria-haspopup="true"
                    onClick={handleMenuOpen}
                    variant="outlined"
                    disableRipple={true}
                    classes={{ root: styles.filterBtn }}
                >
                    <FilterColumnIcon />
                </Button>
                {totalTableInfo && visibleColumns && anchorEl && (
                    <ColumnsListCheckBox
                        anchorEl={anchorEl}
                        handleMenuClose={handleMenuClose}
                        AllBoxSelected={allBoxSelected}
                        handleUnselectApp={handleUnselectApp}
                        handleSelectAll={handleSelectAll}
                        handleColumnToggle={handleColumnToggle}
                        TotalTableInfo={totalTableInfo}
                        fixedColumns={fixedColumns}
                        isColumnVisible={isColumnVisible}
                    />
                )}
            </div>
            <InsightsFilterPopup
                isOpen={isFilterPopupOpen}
                onClose={() => setIsFilterPopupOpen(false)}
            />
        </Box>
    );
};
